import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  useTheme,
  alpha
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Share as ShareIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccessTime as TimeIcon,
  Language as LocationIcon,
  Devices as DeviceIcon,
  Link as ReferrerIcon,
  Refresh as RefreshIcon,
  AccessTime
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { useParams } from 'react-router-dom';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';

// Stores
import usePDFStore from '../../stores/pdfStore';

const PDFAnalytics = () => {
  const theme = useTheme();
  const { id } = useParams();
  const { currentPdf, analytics, loading, fetchPDF, fetchAnalytics } = usePDFStore();
  
  const [dateRange, setDateRange] = useState('7days');
  const [refreshing, setRefreshing] = useState(false);
  
  // Date range options
  const dateRanges = {
    '7days': { label: 'Last 7 days', days: 7 },
    '30days': { label: 'Last 30 days', days: 30 },
    '90days': { label: 'Last 90 days', days: 90 },
    'all': { label: 'All time', days: null }
  };
  
  // Colors for charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];
  
  // Load data
  useEffect(() => {
    if (id) {
      fetchPDF(id);
      loadAnalytics();
    }
  }, [id, dateRange]);
  
  // Load analytics
  const loadAnalytics = async () => {
    const range = dateRanges[dateRange];
    const endDate = new Date();
    const startDate = range.days ? subDays(endDate, range.days) : null;
    
    await fetchAnalytics(id, {
      startDate: startDate ? startOfDay(startDate) : undefined,
      endDate: endOfDay(endDate)
    });
  };
  
  // Refresh analytics
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAnalytics();
    setRefreshing(false);
  };
  
  // Format number with K/M suffix
  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };
  
  // Calculate percentage change
  const calculateChange = (current, previous) => {
    if (!previous) return 0;
    return ((current - previous) / previous) * 100;
  };
  
  if (!currentPdf || !analytics) {
    return null;
  }
  
  // Prepare chart data
  const timeSeriesData = analytics.timeSeries?.map(item => {
    const data = {
      date: format(new Date(item._id), 'MMM d'),
      views: 0,
      downloads: 0,
      shares: 0
    };
    
    // Extract event counts from the events array
    item.events?.forEach(event => {
      if (event.event === 'view') data.views = event.count;
      if (event.event === 'download') data.downloads = event.count;
      if (event.event === 'share') data.shares = event.count;
    });
    
    return data;
  }) || [];
  
  const deviceData = Object.entries(analytics.devices || {}).map(([device, count]) => ({
    name: device.charAt(0).toUpperCase() + device.slice(1),
    value: count
  }));
  
  const browserData = Object.entries(analytics.browsers || {}).map(([browser, count]) => ({
    name: browser,
    value: count
  }));
  
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Analytics: {currentPdf.title}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              label="Date Range"
            >
              {Object.entries(dateRanges).map(([key, range]) => (
                <MenuItem key={key} value={key}>{range.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} disabled={refreshing}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      {loading || refreshing ? <LinearProgress sx={{ mb: 2 }} /> : null}
      
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="caption" display="block">
                    Total Views
                  </Typography>
                  <Typography variant="h4">
                    {formatNumber(analytics.summary?.totalViews || 0)}
                  </Typography>
                  {analytics.previousPeriod && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      {calculateChange(analytics.summary.totalViews, analytics.previousPeriod.totalViews) >= 0 ? (
                        <TrendingUpIcon color="success" fontSize="small" />
                      ) : (
                        <TrendingDownIcon color="error" fontSize="small" />
                      )}
                      <Typography variant="caption" color={calculateChange(analytics.summary.totalViews, analytics.previousPeriod.totalViews) >= 0 ? 'success.main' : 'error.main'}>
                        {Math.abs(calculateChange(analytics.summary.totalViews, analytics.previousPeriod.totalViews)).toFixed(1)}%
                      </Typography>
                    </Box>
                  )}
                </Box>
                <ViewIcon sx={{ color: 'primary.main', fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="caption" display="block">
                    Downloads
                  </Typography>
                  <Typography variant="h4">
                    {formatNumber(analytics.summary?.downloads || 0)}
                  </Typography>
                </Box>
                <DownloadIcon sx={{ color: 'success.main', fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="caption" display="block">
                    Prints
                  </Typography>
                  <Typography variant="h4">
                    {formatNumber(analytics.summary?.prints || 0)}
                  </Typography>
                </Box>
                <PrintIcon sx={{ color: 'warning.main', fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="caption" display="block">
                    Shares
                  </Typography>
                  <Typography variant="h4">
                    {formatNumber(analytics.summary?.shares || 0)}
                  </Typography>
                </Box>
                <ShareIcon sx={{ color: 'info.main', fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Additional Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="caption" display="block">
                    Avg Time Spent
                  </Typography>
                  <Typography variant="h4">
                    {Math.floor((analytics.summary?.avgTimeSpent || 0) / 60)}:{String((analytics.summary?.avgTimeSpent || 0) % 60).padStart(2, '0')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    minutes
                  </Typography>
                </Box>
                <TimeIcon sx={{ color: 'secondary.main', fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="caption" display="block">
                    Total Clicks
                  </Typography>
                  <Typography variant="h4">
                    {formatNumber(analytics.summary?.totalClicks || 0)}
                  </Typography>
                </Box>
                <DeviceIcon sx={{ color: 'warning.main', fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="caption" display="block">
                    Scroll Completion
                  </Typography>
                  <Typography variant="h4">
                    {Math.round(analytics.summary?.scrollCompletion || 0)}%
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ color: 'success.main', fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="caption" display="block">
                    Total Time Spent
                  </Typography>
                  <Typography variant="h4">
                    {Math.floor((analytics.summary?.totalTimeSpent || 0) / 3600)}h
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {Math.floor(((analytics.summary?.totalTimeSpent || 0) % 3600) / 60)}m
                  </Typography>
                </Box>
                <AccessTime sx={{ color: 'info.main', fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Charts */}
      <Grid container spacing={3}>
        {/* Time Series Chart */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Activity Over Time
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={timeSeriesData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <ChartTooltip />
                <Legend />
                <Line type="monotone" dataKey="views" stroke="#8884d8" name="Views" />
                <Line type="monotone" dataKey="downloads" stroke="#82ca9d" name="Downloads" />
                <Line type="monotone" dataKey="shares" stroke="#ffc658" name="Shares" />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        
        {/* Device & Browser Charts */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Devices
            </Typography>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={deviceData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {deviceData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <ChartTooltip />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Browsers
            </Typography>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={browserData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <ChartTooltip />
                <Bar dataKey="value" fill="#8884d8">
                  {browserData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        
        {/* Geographic Data */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Geographic Distribution
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Country</TableCell>
                    <TableCell>City</TableCell>
                    <TableCell align="right">Views</TableCell>
                    <TableCell align="right">Unique Views</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analytics.geographic?.map((geo, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocationIcon fontSize="small" color="action" />
                          {geo.country || 'Unknown'}
                        </Box>
                      </TableCell>
                      <TableCell>{geo.cities?.join(', ') || 'Unknown'}</TableCell>
                      <TableCell align="right">{geo.views}</TableCell>
                      <TableCell align="right">{geo.uniqueViews || 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
        
        {/* Referrers */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Top Referrers
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Source</TableCell>
                    <TableCell align="right">Visits</TableCell>
                    <TableCell align="right">% of Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analytics.referrers?.map((referrer, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ReferrerIcon fontSize="small" color="action" />
                          {referrer.referrer || 'Direct'}
                        </Box>
                      </TableCell>
                      <TableCell align="right">{referrer.views}</TableCell>
                      <TableCell align="right">
                        {analytics.summary?.totalViews ? 
                          ((referrer.views / analytics.summary.totalViews) * 100).toFixed(1) + '%' : 
                          '0%'
                        }
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
        
        {/* Click Analytics */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Most Clicked Elements
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Element</TableCell>
                    <TableCell align="right">Total Clicks</TableCell>
                    <TableCell align="right">Unique Sessions</TableCell>
                    <TableCell>Sample Content</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analytics.clickData?.map((click, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DeviceIcon fontSize="small" color="action" />
                          {click.element || 'Unknown'}
                        </Box>
                      </TableCell>
                      <TableCell align="right">{click.clicks}</TableCell>
                      <TableCell align="right">{click.uniqueClicks}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ 
                          maxWidth: 200, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {click.sampleTexts?.[0] || 'No text content'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
        
        {/* URL Parameters */}
        {analytics.urlParameters && analytics.urlParameters.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                URL Parameters
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Parameter</TableCell>
                      <TableCell align="right">Total Count</TableCell>
                      <TableCell>Top Values</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analytics.urlParameters?.map((param, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ReferrerIcon fontSize="small" color="action" />
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {param.parameter}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">{param.totalCount}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {param.values?.slice(0, 5).map((value, idx) => (
                              <Chip
                                key={idx}
                                label={`${value.value} (${value.count})`}
                                size="small"
                                variant="outlined"
                                sx={{ maxWidth: 200 }}
                              />
                            ))}
                            {param.values?.length > 5 && (
                              <Chip
                                label={`+${param.values.length - 5} more`}
                                size="small"
                                color="default"
                              />
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}
        
        {/* Recent Activity */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Activity
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Event</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Device</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analytics.recentEvents?.map((event, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TimeIcon fontSize="small" color="action" />
                          {format(new Date(event.timestamp), 'MMM d, h:mm a')}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={event.event} 
                          size="small"
                          color={
                            event.event === 'view' ? 'primary' :
                            event.event === 'download' ? 'success' :
                            event.event === 'print' ? 'warning' :
                            'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {event.metadata?.country || 'Unknown'}, {event.metadata?.city || 'Unknown'}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DeviceIcon fontSize="small" color="action" />
                          {event.metadata?.device || 'Unknown'}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PDFAnalytics;